import { GOOGLE_API_KEY } from '../helpers/constants';
import type { LoaderOptions } from '../google/loader/googleMapsLoader';
import { GoogleMapsLoader } from '../google/loader/googleMapsLoader';

import type {
  Route,
  RouteSegmentBoundaries,
  RouteSegmentByConnectionType,
  RouteWithDetails,
} from './types';
import {
  getDirectionsRequest,
  getRouteSegmentBoundaries,
  getRouteSegmentsByConnectionType,
  getRouteWithDetailsFromDirectionsResult,
  isRoutableConnectionType,
} from './helpers';
import { RouteCache } from './routeCache';

export class DirectionsService {
  private loadedService: Promise<google.maps.DirectionsService>;
  private routeCache = RouteCache.getInstance();

  constructor(language: LoaderOptions['language'] = 'en') {
    this.loadedService = new Promise((resolve, reject) => {
      const loader = new GoogleMapsLoader(GOOGLE_API_KEY, {
        language,
      });

      loader
        .loadLibrary('routes')
        .then(({ DirectionsService }) => resolve(new DirectionsService()))
        .catch((error) => reject(error));
    });
  }

  public async calculateRoute(route: Route): Promise<RouteWithDetails> {
    const routeSegmentsByConnectionType: RouteSegmentByConnectionType[] =
      getRouteSegmentsByConnectionType(route);

    const routeSegmentsBoundaries = routeSegmentsByConnectionType.map(
      getRouteSegmentBoundaries
    );

    const calculatedRoutesRequests = routeSegmentsBoundaries.map(
      async (segmentBoundaries) =>
        await this.getRouteWithDetails(segmentBoundaries)
    );

    const calculatedRoutesResults = await Promise.all(calculatedRoutesRequests);

    return calculatedRoutesResults.flat();
  }

  private async getRouteWithDetails(
    segmentBoundaries: RouteSegmentBoundaries
  ): Promise<RouteWithDetails> {
    if (!isRoutableConnectionType(segmentBoundaries.type)) {
      return Promise.resolve([
        {
          from: segmentBoundaries.from,
          to: segmentBoundaries.to,
          type: segmentBoundaries.type,
          expectedTravelTimeInMinutes: undefined,
          distanceInKilometers: undefined,
        },
      ]);
    }

    const cachedResult = this.routeCache.getRouteFromCache(segmentBoundaries);

    if (cachedResult) {
      return Promise.resolve(
        getRouteWithDetailsFromDirectionsResult(cachedResult, segmentBoundaries)
      );
    }

    const request = getDirectionsRequest(segmentBoundaries);

    const service = await this.loadedService;

    return new Promise((resolve, reject) => {
      service.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          this.routeCache.addRouteInCache(segmentBoundaries, result);

          resolve(
            getRouteWithDetailsFromDirectionsResult(result, segmentBoundaries)
          );
        } else {
          reject(
            new Error(
              `Route was not successful for the following reason: ${status}`
            )
          );
        }
      });
    });
  }
}
